/* Compatible complete PCs generated for a selected budget. Prices use the shared storefront pricing service. */
(() => {
  'use strict';
  const script = document.currentScript;
  let catalog = null;
  let labels = {};
  let images = {};
  try {
    const initial = JSON.parse(document.querySelector('[data-elite-collection-configurator-payload]')?.textContent || '{}');
    labels = initial.componentLabels || {};
    images = initial.componentImages || {};
  } catch { /* The sidebar can supply its labels after hydration. */ }
  let revision = 0;
  let caches = new WeakMap();
  let queue = [];
  let scheduled = false;
  let changedTimer = 0;
  let runtimePromise = null;
  let runtimeLoading = false;
  let runtimeError = false;
  let activeQuery = '';
  let waitingForCatalog = false;
  let catalogError = false;
  const pending = { selections: {}, price: null, priceState: 'pending', autoBudget: true };
  const bands = {
    'under-800': [0, 80000], '800-1000': [80000, 100000], '1000-1500': [100000, 150000],
    '1500-2000': [150000, 200000], '2000-plus': [200000, Infinity], '2500-plus': [250000, Infinity],
    '3000-plus': [300000, Infinity], '800-1200': [80000, 120000], '1200-1800': [120000, 180000],
    '1800-2500': [180000, 250000]
  };
  function ranges(values) {
    const merged = [];
    for (const range of values.map(value => bands[value]).filter(Boolean).sort((a, b) => a[0] - b[0])) {
      const previous = merged[merged.length - 1];
      if (previous && range[0] <= previous[1]) previous[1] = Math.max(previous[1], range[1]);
      else merged.push([...range]);
    }
    return merged;
  }
  function selectedRanges() {
    return [...document.querySelectorAll('[data-collection-filter="budget"]:checked')].map(input => input.value).filter(value => bands[value]);
  }
  function notify() {
    if (changedTimer) return;
    changedTimer = setTimeout(() => {
      changedTimer = 0;
      document.dispatchEvent(new CustomEvent('elite:budget-builds:updated'));
    }, 120);
  }
  function setCatalog(next) {
    if (next === catalog) return;
    catalog = next;
    waitingForCatalog = false;
    catalogError = false;
    runtimeError = false;
    revision++;
    caches = new WeakMap();
    queue = [];
    notify();
  }
  function catalogFailed() {
    catalogError = true;
    waitingForCatalog = false;
    notify();
  }
  function cancel() {
    for (const job of queue) job.cache.delete(job.key);
    queue = [];
    activeQuery = '';
    waitingForCatalog = false;
  }
  async function run() {
    // Full component searches are expensive; keep each request small enough
    // to finish without discarding every case when one batch times out.
    const jobs = queue.splice(0, 2);
    if (!jobs.length) { scheduled = false; return; }
    const first = jobs[0];
    try {
      // Session renewal has its own deadline and must not consume the build
      // search's time allowance before that request has even started.
      await window.ElitePCPrivate.prepare();
      if (!jobs.some(job => job.revision === revision && job.query === activeQuery)) return;
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 30000);
      let response;
      try { response = await window.ElitePCPrivate.request('budget', {
        cases:jobs.map(job => job.caseSku), entries:first.entries, budgets:first.budgets,
        processorBrand:first.context.processorBrand || '', stockMode:first.context.stockMode || 'in_stock',
        categorySearch:first.context.categorySearch === true
      }, {signal:controller.signal}); } finally { clearTimeout(timeout); }
      for (const job of jobs) {
        if (job.revision !== revision || job.query !== activeQuery) { job.cache.delete(job.key); continue; }
        const result = response.builds?.find(entry => entry.caseSku === job.caseSku)?.build;
        // The server has checked the whole selection and its retail price.
        // A separate label/options refresh must not hold up these results.
        const checked = result?.price;
        const price = checked?.currency==='GBP' && Number.isFinite(checked.amount) && checked.amount>0
          && Number.isSafeInteger(checked.pennies) && checked.pennies===Math.round(checked.amount*100)
          ? {...checked,formatted:new Intl.NumberFormat('en-GB',{style:'currency',currency:'GBP'}).format(checked.amount)} : null;
        const matches = price && ranges(job.budgets).some(([min,max]) => price.pennies >= min && price.pennies < max);
        job.cache.set(job.key, matches ? {...result, price, autoBudget:!job.context.categorySearch, budgetSource:'checked', priceState:'ready'} : null);
      }
    } catch (error) {
      if (!jobs.some(job => job.revision === revision && job.query === activeQuery)) return;
      document.documentElement.dataset.eliteBudgetCheckError=String(error?.status || error?.name || 'unavailable');
      for (const job of jobs) job.cache.set(job.key, null);
      runtimeError = true;
      for (const job of queue) job.cache.set(job.key, null);
      queue = [];
    } finally { scheduled = false; notify(); schedule(); }
  }
  function schedule() {
    if (scheduled || !queue.length) return;
    scheduled = true;
    // Yield between cases so filtering and navigation remain responsive.
    setTimeout(run, 0);
  }
  function preparedBuild(pricing, caseSku, entries, context, budgets) {
    const ready=window.ElitePCPrepared?.choose(caseSku,entries,context,budgets);
    if(ready!==undefined)return ready;
    if (window.ElitePCS?.privatePricing) return undefined;
    const index = window.ElitePCBudgetSelections;
    if (index?.version !== 1 || pricing?.priceState !== 'ready') return undefined;
    const prepared = index.cases?.[caseSku];
    if (!Array.isArray(prepared)) return undefined;
    const candidates = [];
    for (const id of prepared) {
      const recipe = index.recipes[id];
      if (!Array.isArray(recipe) || recipe.length !== index.keys.length) continue;
      const selections = {case: caseSku};
      index.keys.forEach((key, offset) => { if (recipe[offset]) selections[key] = recipe[offset]; });
      if (entries.some(entry => {
        const key = entry.param.replace(/^(?:elite_|component_)/, '');
        return /^__no_/.test(entry.value) ? Boolean(selections[key]) : selections[key] !== entry.value;
      })) continue;
      if (context.processorBrand && pricing.componentOptions.cpu?.[selections.cpu]?.brand !== context.processorBrand) continue;
      const stocked = Object.entries(selections).every(([key, sku]) => {
        const option = pricing.componentOptions[key]?.[sku];
        if (!option || !Number.isFinite(option.priceContribution)) return false;
        if (key === 'case' && ['all', 'out_of_stock'].includes(context.stockMode)) return true;
        // The configured digital licence and draft-only power lead are priced
        // by the page's public mirror; hardware must have current native stock.
        if (['windows_key', 'kettle_lead'].includes(key) && option.available == null) return true;
        return option.available === true;
      });
      if (!stocked) continue;
      const price = window.EliteCollectionPCPrices.price(pricing, selections);
      if (price) candidates.push({selections, price, priceState:'ready', autoBudget:true, budgetSource:'prepared'});
    }
    for (const [minimum, maximum] of ranges(budgets)) {
      const target = Number.isFinite(maximum) ? maximum : minimum + 50000;
      const matches = candidates.filter(build => build.price.pennies >= minimum && build.price.pennies < maximum);
      matches.sort((a,b) => {
        const aUnder = a.price.pennies < target, bUnder = b.price.pennies < target;
        return aUnder !== bUnder ? (aUnder ? -1 : 1)
          : aUnder ? b.price.pennies-a.price.pennies : a.price.pennies-b.price.pennies;
      });
      if (matches.length) return matches[0];
    }
    // An ordinary budget click is a synchronous filter over checked builds.
    // Unprepared component constraints still use the private search service.
    return entries.every(entry => /^__no_/.test(entry.value)) && !context.processorBrand ? null : undefined;
  }
  function get(pricing, caseSku, entries, context, budgets) {

    const constraints = entries.filter(entry => /^(?:elite_|component_)(?:mobo|cpu|ram|gpu|psu|cooler|wifi|sata_ssd|nvme_ssd|sata_hdd|windows_key|kettle_lead)$/.test(entry.param))
      .map(entry => ({ param: entry.param, value: entry.value })).sort((a, b) => a.param.localeCompare(b.param));
    const query = JSON.stringify([revision, [...budgets].sort(), constraints, context.processorBrand || '', context.gpuFamily || '', context.stockMode || 'in_stock', context.categorySearch === true]);
    if (query !== activeQuery) { cancel(); activeQuery = query; runtimeError = false; delete document.documentElement.dataset.eliteBudgetCheckError; }
    let cache = caches.get(pricing);
    if (!cache) { cache = new Map(); caches.set(pricing, cache); }
    const key = JSON.stringify([query, caseSku]);
    if (cache.has(key)) return cache.get(key);
    const prepared = preparedBuild(pricing, caseSku, constraints, context, budgets);
    if (prepared !== undefined) { cache.set(key, prepared); return prepared; }
    if (runtimeError) return null;
    cache.set(key, pending);
    queue.push({ cache, key, query, revision, pricing, caseSku, entries: constraints, context, budgets });
    schedule();
    return pending;
  }
  function busy(pricing) {
    return !runtimeError && (queue.length > 0 || scheduled);
  }
  function failed(pricing) { return runtimeError; }
  function label(key, sku) {
    if (!sku) return '';
    if (labels[key]?.[sku]) return labels[key][sku];
    const option = window.ElitePCPrepared?.option(key,sku) || catalog?.componentGroups.find(group => group.key === key)?.options.find(option => option.sku === sku);
    const text = option?.label || sku;
    return key === 'ram' && /^\d+$/.test(text) ? text + 'GB' : text;
  }
  function paint(card, build) {
    if (build?.price) writeLinks(card, build);
    if ((!build?.autoBudget && !['prepared','checked'].includes(build?.budgetSource)) || !build.price) {
      delete card.dataset.eliteBudgetBuild;
      delete card.dataset.eliteBudgetSelections;
      return;
    }
    if (card.dataset.eliteBudgetBuild !== 'true') card.dataset.eliteBudgetBuild = 'true';
    const signature = JSON.stringify(build.selections);
    if (card.dataset.eliteBudgetSelections !== signature) card.dataset.eliteBudgetSelections = signature;
    for (const row of card.querySelectorAll('[data-configurator-summary-value]')) {
      const key = row.dataset.configuratorSummaryValue;
      const value = row;
      const text = label(key, build.selections[key]) || (key === 'sata_hdd' ? 'No HDD' : key === 'sata_ssd' ? 'No SATA SSD' : 'None selected');
      if (value.textContent !== text) value.textContent = text;
    }
    const photos = {...images};
    for (const [key, sku] of Object.entries(build.selections)) {
      const native = window.ElitePCPrepared?.option(key,sku)?.images?.[0] || window.ElitePCPagePrice?.nativePrices?.[sku]?.[2];
      if (native) photos[key] = {...photos[key], [sku]: native};
    }
    window.EliteCollectionComponentData?.renderCardSummary(card, build.selections, { componentLabels: labels, componentImages: photos });
  }
  function writeLinks(card, build) {
    for (const anchor of card.querySelectorAll('a[href*="/products/"]')) {
      const url = new URL(anchor.getAttribute('href'), location.origin);
      for (const key of [...url.searchParams.keys()]) if (/^(?:elite_|component_)/.test(key)) url.searchParams.delete(key);
      for (const [key, sku] of Object.entries(build.selections)) url.searchParams.set('elite_' + key, sku);
      url.searchParams.set('pc', build.autoBudget ? 'gaming-pc-budget' : 'gaming-pc');
      const href = url.pathname + url.search + url.hash;
      if (anchor.getAttribute('href') !== href) anchor.setAttribute('href', href);
    }
  }
  window.ElitePCBudgetBuilds = { ranges, selectedRanges, get, setCatalog, catalogFailed, setLabels: (value, photos) => { labels = value || {}; images = photos || {}; }, paint, busy, failed, cancel };

})();
